import { Box, Typography } from "@mui/material";
import { APP_VERSION } from "../../utils/data_variables";

export const ink = "#0B0A08";
export const gold = "#C8A459";
export const goldSoft = "#E8D5A3";
export const canvas = "#F6F5F1";
export const muted = "#6E665A";
export const line = "#E5E2DA";

const logo = "https://inovartgold.com.br/assets/logo/logo.jpg";
const spithaUrl = "https://spitha.com.br/";

export default function AuthSplitLayout({ title, lead, children }) {
    return (
        <Box component="main" sx={styles.shell}>
            <Box sx={styles.brandPane}>
                <Box sx={styles.rings} aria-hidden>
                    <Box sx={styles.ringOuter} />
                    <Box sx={styles.ringInner} />
                    <Box sx={styles.ringCore} />
                </Box>

                <Box sx={styles.brandInner}>
                    <Typography component="p" sx={styles.eyebrow}>
                        Inovart Gold
                    </Typography>
                    <Typography component="h1" sx={styles.brandTitle}>
                        Joalheria feita para durar.
                    </Typography>
                    <Typography sx={styles.brandLead}>
                        Área restrita. Entre com sua conta para continuar.
                    </Typography>
                </Box>
            </Box>

            <Box sx={styles.formPane}>
                <Box sx={styles.formInner}>
                    <Box
                        component="img"
                        src={logo}
                        alt="Inovart Gold"
                        sx={styles.logo}
                    />
                    <Typography component="h2" sx={styles.formTitle}>
                        {title}
                    </Typography>
                    {lead ? (
                        <Typography sx={styles.formLead}>{lead}</Typography>
                    ) : null}
                    {children}
                    <Box sx={styles.meta}>
                        <Typography sx={styles.metaText}>
                            Desenvolvido por{" "}
                            <Box
                                component="a"
                                href={spithaUrl}
                                target="_blank"
                                rel="noreferrer"
                                sx={styles.metaLink}
                            >
                                Spítha
                            </Box>
                        </Typography>
                        <Typography sx={styles.metaText}>Versão {APP_VERSION}</Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export const authFormSx = {
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    "& .MuiOutlinedInput-root": {
        bgcolor: "#fff",
        "& fieldset": {
            borderColor: line,
        },
        "&:hover fieldset": {
            borderColor: gold,
        },
        "&.Mui-focused fieldset": {
            borderColor: gold,
            borderWidth: 1,
        },
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: ink,
    },
    "& .MuiFormHelperText-root": {
        textAlign: "left",
        mx: 0,
    },
};

export const authSubmitSx = {
    mt: 0.5,
    minHeight: 48,
    bgcolor: gold,
    color: "#fff",
    boxShadow: "none",
    letterSpacing: "0.02em",
    "&:hover": {
        bgcolor: "#9E7C38",
        boxShadow: "none",
    },
    "&:active": {
        bgcolor: "#8A6C30",
    },
    "&:focus-visible": {
        outline: `2px solid ${ink}`,
        outlineOffset: 2,
    },
    "&.Mui-disabled": {
        bgcolor: gold,
        color: "#fff",
        opacity: 0.55,
    },
};

export const authLinkSx = {
    mt: 0.5,
    alignSelf: "flex-start",
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    fontWeight: 500,
    color: ink,
    textDecoration: "none",
    borderBottom: `1px solid ${gold}`,
    "&:hover": {
        color: "#9E7C38",
    },
    "&:focus-visible": {
        outline: `2px solid ${gold}`,
        outlineOffset: 3,
    },
};

export const authEyeSx = {
    color: muted,
    "&:hover": {
        color: ink,
    },
    "&:focus-visible": {
        outline: `2px solid ${gold}`,
        outlineOffset: 2,
    },
};

const styles = {
    shell: {
        display: "flex",
        height: "100dvh",
        maxHeight: "100dvh",
        overflow: "hidden",
        bgcolor: canvas,
        "@media (prefers-reduced-motion: reduce)": {
            "& *": {
                animation: "none !important",
                transition: "none !important",
            },
        },
    },
    brandPane: {
        display: { xs: "none", lg: "flex" },
        position: "relative",
        flex: "1 1 50%",
        height: "100%",
        overflow: "hidden",
        bgcolor: ink,
        color: canvas,
        px: { md: 5, lg: 8 },
        py: { md: 4, lg: 6 },
        alignItems: "center",
        "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
                "linear-gradient(160deg, rgba(200,164,89,0.16) 0%, rgba(11,10,8,0) 42%, rgba(11,10,8,0.35) 100%)",
            pointerEvents: "none",
        },
    },
    rings: {
        position: "absolute",
        right: { md: "-18%", lg: "-8%" },
        top: "50%",
        width: 520,
        height: 520,
        transform: "translateY(-50%)",
        pointerEvents: "none",
    },
    ringOuter: {
        position: "absolute",
        inset: 0,
        borderRadius: "50%",
        border: `1px solid ${gold}`,
        opacity: 0.28,
    },
    ringInner: {
        position: "absolute",
        inset: "56px",
        borderRadius: "50%",
        border: `1px solid ${goldSoft}`,
        opacity: 0.4,
    },
    ringCore: {
        position: "absolute",
        inset: "132px",
        borderRadius: "50%",
        border: `2px solid ${gold}`,
        opacity: 0.55,
        boxShadow: "0 0 80px rgba(200,164,89,0.18)",
    },
    brandInner: {
        position: "relative",
        zIndex: 1,
        maxWidth: 440,
    },
    eyebrow: {
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: gold,
        mb: 2,
        textAlign: "left",
    },
    brandTitle: {
        fontFamily: '"DM Serif Display", Georgia, serif',
        fontWeight: 400,
        fontSize: { md: "2.25rem", lg: "2.75rem" },
        lineHeight: 1.12,
        letterSpacing: "-0.02em",
        color: canvas,
        textWrap: "balance",
        textAlign: "left",
        mb: 2,
        "@media (max-height: 760px)": {
            fontSize: "2rem",
        },
    },
    brandLead: {
        fontFamily: "Inter, sans-serif",
        fontSize: 15,
        lineHeight: 1.55,
        color: "rgba(246,245,241,0.72)",
        textAlign: "left",
        textWrap: "pretty",
        mb: 3,
        maxWidth: 400,
        "@media (max-height: 760px)": {
            mb: 2,
            fontSize: 14,
        },
    },
    formPane: {
        flex: { xs: "1 1 100%", lg: "1 1 50%" },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        overflow: "hidden",
        px: { xs: 3, sm: 5, md: 6 },
        py: { xs: 3, md: 4 },
        bgcolor: canvas,
    },
    formInner: {
        width: "100%",
        maxWidth: 400,
    },
    logo: {
        display: "block",
        height: 56,
        width: "auto",
        borderRadius: 1,
        mb: 4,
        objectFit: "contain",
    },
    formTitle: {
        fontFamily: '"DM Serif Display", Georgia, serif',
        fontSize: { xs: "1.75rem", md: "2rem" },
        fontWeight: 400,
        color: ink,
        textAlign: "left",
        lineHeight: 1.15,
        mb: 1,
    },
    formLead: {
        fontFamily: "Inter, sans-serif",
        fontSize: 15,
        color: muted,
        textAlign: "left",
        mb: 4,
    },
    meta: {
        mt: 5,
        display: "flex",
        justifyContent: "space-between",
        gap: 2,
        borderTop: `1px solid ${line}`,
        pt: 2,
    },
    metaText: {
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        color: muted,
        textAlign: "left",
    },
    metaLink: {
        color: ink,
        textDecoration: "none",
        fontWeight: 500,
        "&:hover": {
            color: gold,
        },
    },
};
